import { useEffect, useState, type MutableRef } from "preact/hooks";

export function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

export function progressOf(top: number, height: number, viewport: number): number {
  const distance = height - viewport;
  return distance > 0 ? clamp(-top / distance, 0, 1) : 0;
}

export function stepFor(progress: number, steps: number): number {
  return clamp(Math.floor(progress * steps), 0, steps - 1);
}

export function offsetFor(step: number, steps: number, distance: number): number {
  return Math.round(((step + 0.5) / steps) * distance);
}

export function countAt(target: number, elapsed: number, duration: number): number {
  const t = duration > 0 ? clamp(elapsed / duration, 0, 1) : 1;
  return Math.round(target * (1 - (1 - t) ** 3));
}

function prefersReducedMotion(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof matchMedia !== "function") {
      return;
    }
    const query = matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);
  return reduced;
}

export function useInView<T extends Element>(ref: MutableRef<T | null>, margin = "0px"): boolean {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setInView(Boolean(entry?.isIntersecting)),
      { rootMargin: margin },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, margin]);
  return inView;
}

export function useTicker(interval: number, running: boolean): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!running || prefersReducedMotion()) {
      return;
    }
    const timer = setInterval(() => {
      if (document.visibilityState !== "hidden") {
        setTick((value) => value + 1);
      }
    }, interval);
    return () => clearInterval(timer);
  }, [interval, running]);
  return tick;
}
